import { createFileRoute, notFound } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'

import { isDevSimRouteEnabled } from '@/lib/dev-sim-access'

/**
 * The dev Simulation Console (account-management v2) — top-level route, outside
 * `_app` so it skips the Shell + WorkspaceGate (the `internal.tsx` precedent): scenarios
 * that expire a plan or force `past_due` must not lock the tester out of the very
 * tool that produced that state.
 *
 * Triple-gate #3 (roadmap §2 · spec §3) — two independent guarantees:
 *   1. Runtime inertness — `beforeLoad` throws `notFound()` (→ root notFoundComponent)
 *      unless either local auth bypass is active or the account-v2 sandbox sim flag is active.
 *   2. Tree-shaking — normal prod builds collapse to the `() => null` stub and Rollup drops the
 *      dead branch's dynamic `import('@/features/dev-sim')`. The account-v2 sandbox build sets
 *      `VITE_ACCOUNT_V2_SANDBOX_SIM=1`, so that one production-mode bundle intentionally keeps the
 *      console chunk.
 */
const shouldBundleSimConsole =
  !import.meta.env.PROD ||
  (import.meta.env.VITE_ACCOUNT_V2_SANDBOX_SIM === '1' &&
    import.meta.env.VITE_TEST_AUTH === '1' &&
    import.meta.env.VITE_USE_MOCKS !== '1')

const SimConsolePage = !shouldBundleSimConsole
  ? () => null
  : lazy(() => import('@/features/dev-sim').then((m) => ({ default: m.SimConsolePage })))

function DevSimRoute() {
  return (
    <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading console…</div>}>
      <SimConsolePage />
    </Suspense>
  )
}

// If the lazy `import('@/features/dev-sim')` chunk fails (HMR churn / a stale chunk
// after a rebuild), the rejected promise surfaces here — this top-level route sits
// outside `_app`, so it inherits no Shell error boundary. Offer a reload instead of a
// blank screen.
function DevSimError() {
  return (
    <div className="p-6 text-sm text-muted-foreground">
      Failed to load the Simulation Console.{' '}
      <button type="button" className="underline" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  )
}

export const Route = createFileRoute('/dev-sim')({
  beforeLoad: () => {
    if (!isDevSimRouteEnabled()) throw notFound()
  },
  component: DevSimRoute,
  errorComponent: DevSimError,
})
